/**
 * Chiamate alle rotte di autenticazione del gateway (il BFF).
 *
 * Contratto in `.claude/handoff/06-cantiere-autenticazione-bff.md`. Questo file
 * è la sola traduzione di quel contratto in TypeScript: se una risposta del
 * gateway cambia forma, cambia qui e da nessun'altra parte.
 *
 * Provvisorio dichiarato, come per `api/projects.ts`: i tipi sono scritti a
 * mano invece che generati dall'OpenAPI del gateway (CLAUDE.md §2.3).
 *
 * Nota di sicurezza: nessuna di queste funzioni restituisce o accetta un token.
 * L'unico materiale sensibile che attraversa il browser è il segreto TOTP
 * dentro `otpauth_uri`, che l'utente deve poter leggere per registrarlo a mano,
 * e i codici di recupero, mostrati una sola volta e mai salvati.
 */

import type { ApiClient } from '../api/client';

/** Ruoli globali riconosciuti dalla piattaforma. */
export type Role = 'ADMIN' | 'PROJECT_ADMIN' | 'USER';

/** Stato della sessione secondo il gateway. */
export type SessionStatus = 'anonymous' | 'pending_enrollment' | 'pending_mfa' | 'active';

/** Come è nata la sessione (ADR 0033): con la password oppure con Google. */
export type LoginMethod = 'password' | 'google';

/** Ruoli che la dashboard riconosce; gli altri vengono scartati. */
const KNOWN_ROLES: readonly string[] = ['ADMIN', 'PROJECT_ADMIN', 'USER'];

/** Prefisso comune delle rotte di autenticazione. */
const BASE = '/api/v1/auth';

/**
 * Opzioni con cui si chiamano le rotte di autenticazione.
 *
 * Su queste rotte un 401 è una risposta prevista (credenziale sbagliata) e non
 * deve far scattare il rientro automatico allo stato anonimo.
 */
const AUTH_ROUTE = { notifyUnauthenticated: false } as const;

/** Risposta di `GET /api/v1/auth/session`. */
export interface SessionResponse {
  /** True quando la sessione è pienamente valida (`status === 'active'`). */
  authenticated: boolean;
  /** Stato della sessione. */
  status: SessionStatus;
  /** Nome utente nel realm, `null` se anonimo. */
  username: string | null;
  /** Ruoli globali dell'utente, già filtrati dal gateway. */
  roles: string[];
  /** True se l'utente ha già registrato un secondo fattore. */
  mfa_enrolled: boolean;
  /** True se il suo ruolo rende la MFA obbligatoria. */
  mfa_required: boolean;
  /**
   * Come è nata la sessione. `null` o assente per una sessione anonima, per una nata
   * prima che il gateway lo registrasse e per i gateway precedenti.
   */
  login_method?: LoginMethod | null;
  /**
   * Impersonazione in corso (ADR 0009), `null` altrimenti. Quando c'è,
   * `username` e `roles` sono quelli dell'utente impersonato. Assente dai
   * gateway precedenti: si tratta come `null`.
   */
  impersonation?: ImpersonationState | null;
  /**
   * True se la sessione può avviare un'impersonazione. Lo decide il gateway:
   * la pagina ci decide solo se mostrare l'azione.
   */
  can_impersonate?: boolean;
  /** Durata massima di un'impersonazione, in minuti. */
  impersonation_max_minutes?: number;
  /** True se l'avvio di un'impersonazione chiede il codice TOTP. */
  impersonation_requires_totp?: boolean;
}

/** Un'impersonazione in corso, come la descrive il gateway. */
export interface ImpersonationState {
  /** Nome utente dell'utente impersonato. */
  user: string;
  /** Ruoli dell'utente impersonato. */
  roles: string[];
  /** Nome utente dell'amministratore che impersona. */
  admin: string;
  /** Inizio, ISO 8601. */
  started_at: string;
  /** Scadenza, ISO 8601: oltre, la sessione torna all'amministratore. */
  expires_at: string;
}

/** Dati con cui un amministratore avvia un'impersonazione. */
export interface ImpersonationRequest {
  /** Identificativo dell'utente da impersonare, dalla directory. */
  subject_id: string;
  /** Motivazione, da 10 a 500 caratteri: finisce nell'audit. */
  reason: string;
  /**
   * Codice della verifica in due passaggi dell'amministratore. Si invia solo se
   * la sessione lo chiede (`impersonation_requires_totp`).
   */
  code?: string;
  /** Durata in minuti, fino al massimo dichiarato dalla sessione. */
  duration_minutes: number;
}

/** Risposta di `POST /api/v1/auth/login`. */
export interface LoginResponse {
  /** Stato in cui si trova la sessione appena creata. */
  status: Exclude<SessionStatus, 'anonymous'>;
}

/** Risposta di `POST /api/v1/auth/mfa/enroll`. */
export interface EnrollmentResponse {
  /** URI `otpauth://` da registrare nell'app di autenticazione. */
  otpauth_uri: string;
  /** QR code già renderizzato dal backend, come data URI PNG. */
  qr_data_uri: string;
  /** Emittente mostrato nell'app di autenticazione. */
  issuer: string;
  /** Nome dell'account mostrato nell'app di autenticazione. */
  account: string;
}

/** Risposta di `POST /api/v1/auth/mfa/enroll/verify`. */
export interface EnrollmentVerifiedResponse {
  /** Stato raggiunto: la registrazione conclusa attiva la sessione. */
  status: 'active';
  /** I dieci codici di recupero, mostrati una sola volta. */
  recovery_codes: string[];
}

/** Risposta di `POST /api/v1/auth/mfa/verify`. */
export interface MfaVerifiedResponse {
  /** Stato raggiunto: la verifica riuscita attiva la sessione. */
  status: 'active';
  /** Codici di recupero ancora spendibili. */
  recovery_codes_left: number;
}

/** Credenziale con cui si supera la verifica del secondo fattore. */
export type MfaCredential =
  | { kind: 'totp'; value: string }
  | { kind: 'recovery'; value: string };

/**
 * Filtra i ruoli riconosciuti dalla dashboard.
 *
 * Un ruolo nuovo nel realm (come il tecnico `mfa-required`) non deve tradursi
 * in una voce di menu abilitata per errore: si scarta tutto ciò che non è nel
 * vocabolario noto, come fa il backend.
 *
 * @param raw Ruoli così come arrivano dal gateway.
 * @returns I soli ruoli riconosciuti.
 */
export function knownRoles(raw: unknown): Role[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((role): role is Role => typeof role === 'string' && KNOWN_ROLES.includes(role));
}

/**
 * Estrae il segreto TOTP da un URI `otpauth://`.
 *
 * Serve a chi non può inquadrare il QR e deve digitare la chiave a mano. Si usa
 * `URL`/`URLSearchParams` invece di una regex per non sbagliare la decodifica
 * percentuale dei parametri.
 *
 * @param otpauthUri URI restituito dal gateway.
 * @returns Il segreto in base32, oppure `null` se l'URI non lo contiene.
 */
export function extractTotpSecret(otpauthUri: string): string | null {
  try {
    const parsed = new URL(otpauthUri);
    const secret = parsed.searchParams.get('secret');
    return secret !== null && secret.length > 0 ? secret : null;
  } catch {
    // Un URI malformato non deve far cadere la pagina: si mostra solo il QR.
    return null;
  }
}

/**
 * Raggruppa il segreto in blocchi di quattro caratteri.
 *
 * Una stringa base32 di 32 caratteri è illeggibile tutta attaccata; i blocchi
 * riducono gli errori di trascrizione e sono il formato che mostrano anche le
 * app di autenticazione.
 *
 * @param secret Segreto base32.
 * @returns Il segreto con uno spazio ogni quattro caratteri.
 */
export function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

/** Rotte di autenticazione del gateway. */
export class AuthApi {
  private readonly client: ApiClient;

  /**
   * @param client Client HTTP condiviso, già configurato con i cookie.
   */
  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Legge lo stato della sessione corrente.
   *
   * @returns Lo stato della sessione; `anonymous` se non ce n'è una.
   * @throws ApiError Se il gateway non risponde con successo.
   */
  async session(): Promise<SessionResponse> {
    return this.client.request<SessionResponse>('GET', `${BASE}/session`, undefined, AUTH_ROUTE);
  }

  /**
   * Apre una sessione con nome utente e password.
   *
   * @param username Nome utente nel realm.
   * @param password Password dell'utente.
   * @returns Lo stato in cui si trova la sessione creata.
   * @throws ApiError 401 se le credenziali non sono valide, 429 se i tentativi
   *   sono troppi (con `retryAfterSeconds` valorizzato).
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    return this.client.request<LoginResponse>(
      'POST',
      `${BASE}/login`,
      { username, password },
      AUTH_ROUTE,
    );
  }

  /**
   * Avvia la registrazione del secondo fattore.
   *
   * @returns QR code e URI `otpauth://` da mostrare all'utente.
   * @throws ApiError Se la sessione non consente la registrazione. Un 401 qui
   *   significa sessione assente, quindi riporta l'applicazione all'accesso.
   */
  async enroll(): Promise<EnrollmentResponse> {
    return this.client.request<EnrollmentResponse>('POST', `${BASE}/mfa/enroll`);
  }

  /**
   * Conferma la registrazione con il primo codice generato dall'app.
   *
   * @param code Codice TOTP a sei cifre.
   * @returns Lo stato attivo e i codici di recupero da mostrare una sola volta.
   * @throws ApiError 401 se il codice non è valido.
   */
  async verifyEnrollment(code: string): Promise<EnrollmentVerifiedResponse> {
    return this.client.request<EnrollmentVerifiedResponse>(
      'POST',
      `${BASE}/mfa/enroll/verify`,
      { code },
      AUTH_ROUTE,
    );
  }

  /**
   * Supera la verifica del secondo fattore su una sessione `pending_mfa`.
   *
   * @param credential Codice TOTP oppure codice di recupero.
   * @returns Lo stato attivo e i codici di recupero rimasti.
   * @throws ApiError 401 se il codice non è valido.
   */
  async verifyMfa(credential: MfaCredential): Promise<MfaVerifiedResponse> {
    const body =
      credential.kind === 'totp' ? { code: credential.value } : { recovery_code: credential.value };
    return this.client.request<MfaVerifiedResponse>(
      'POST',
      `${BASE}/mfa/verify`,
      body,
      AUTH_ROUTE,
    );
  }

  /**
   * Avvia l'impersonazione di un utente (solo amministratori della piattaforma).
   *
   * Il 401 qui è la risposta prevista a un codice sbagliato, non una sessione
   * decaduta: non riporta l'applicazione all'accesso.
   *
   * @param request Utente, motivazione, codice e durata.
   * @returns Lo stato dell'impersonazione appena avviata.
   * @throws ApiError 401 codice errato, 403 utente non impersonabile o
   *   permesso mancante, 422 dati non validi, 429 troppi codici errati.
   */
  async startImpersonation(request: ImpersonationRequest): Promise<ImpersonationState> {
    return this.client.request<ImpersonationState>(
      'POST',
      `${BASE}/impersonation`,
      request,
      AUTH_ROUTE,
    );
  }

  /**
   * Torna all'account dell'amministratore. Idempotente.
   *
   * @returns Nulla.
   * @throws ApiError Se il gateway rifiuta la richiesta.
   */
  async stopImpersonation(): Promise<void> {
    await this.client.request<void>('POST', `${BASE}/impersonation/stop`);
  }

  /**
   * Chiude la sessione: il gateway cancella il cookie e revoca la sessione
   * Keycloak.
   *
   * @returns Nulla.
   * @throws ApiError Se il gateway rifiuta la richiesta.
   */
  async logout(): Promise<void> {
    await this.client.request<void>('POST', `${BASE}/logout`, undefined, AUTH_ROUTE);
  }
}
